import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useApi } from "../../../hooks/useApi";
import { allDashWidgetsOn, normalizeDashWidgets } from "../dashboardLayout";

/**
 * Org-wide Pulse card visibility. GET any member; PUT org_admin / main_admin.
 * Missing keys stay ON so a failed read never blanks the page.
 */
export function useDashboardLayout() {
  const api = useApi();
  const [widgets, setWidgets] = useState(allDashWidgetsOn);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let dead = false;
    api.get("/api/v1/organizations/dashboard-layout")
      .then((r) => {
        if (dead) return;
        setWidgets(normalizeDashWidgets(r.data?.data?.layout));
      })
      .catch(() => { /* stay on defaults — never hide cards because of a read fail */ })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, [api]);

  const persist = useCallback(async (next, prev) => {
    if (!Object.values(next).some(Boolean)) {
      toast.error("Keep at least one card");
      if (prev) setWidgets(prev);
      return false;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/api/v1/organizations/dashboard-layout", { widgets: next });
      setWidgets(normalizeDashWidgets(data?.data?.layout || { widgets: next }));
      return true;
    } catch (_e) {
      if (prev) setWidgets(prev);
      toast.error("Could not save dashboard");
      return false;
    } finally {
      setSaving(false);
    }
  }, [api]);

  const setWidget = useCallback((id, on) => {
    setWidgets((current) => {
      const next = { ...current, [id]: on };
      persist(next, current);
      return Object.values(next).some(Boolean) ? next : current;
    });
  }, [persist]);

  const reset = useCallback(() => {
    setWidgets((current) => {
      const next = allDashWidgetsOn();
      persist(next, current);
      return next;
    });
  }, [persist]);

  return { widgets, loading, saving, setWidget, reset };
}

export default useDashboardLayout;
